/**
 * Filter Flair cart count synchroniser.
 *
 * Warehouse owns cart mutations and section rendering. This small adapter keeps
 * every header badge in sync and exposes ff:cart-updated for first-party
 * integrations such as Klaviyo. It deliberately contains no drawer-app hooks.
 */

(function () {
  'use strict';

  var COUNT_SELECTOR = '.header__cart-count';
  var syncTimer = null;
  var requestInFlight = null;
  var lastKnownCount = null;
  var legacyCartMode = Boolean(window.ffCartExperiment && !window.ffCartExperiment.enabled);

  function parseCount(value) {
    var count = parseInt(value, 10);
    return Number.isNaN(count) ? 0 : count;
  }

  function countElements() {
    return Array.prototype.slice.call(document.querySelectorAll(COUNT_SELECTOR));
  }

  function updateElement(element, itemCount) {
    var isEmpty = itemCount === 0;
    var toggle = element.closest('.header__cart-toggle');
    var action = element.closest('.header__action-item--cart');

    element.textContent = itemCount;
    element.classList.toggle('header__cart-count--empty', isEmpty);
    element.classList.toggle('header__cart-count--has-items', !isEmpty);
    element.classList.toggle('ff-basket-count--empty', isEmpty);

    if (toggle) {
      toggle.classList.toggle('ff-basket-btn', !isEmpty);
      toggle.classList.toggle('ff-basket-empty', isEmpty);
      toggle.classList.toggle('ff-icon-btn', isEmpty);
      toggle.setAttribute('aria-label', 'Basket — ' + itemCount + (itemCount === 1 ? ' item' : ' items'));
    }

    if (action) action.classList.toggle('ff-basket-has-items', !isEmpty);
  }

  function publish(itemCount, source) {
    document.documentElement.dispatchEvent(new CustomEvent('ff:cart-updated', {
      bubbles: true,
      detail: {
        itemCount: itemCount,
        previousItemCount: lastKnownCount,
        source: source || 'unknown'
      }
    }));
    lastKnownCount = itemCount;
  }

  function render(itemCount, source) {
    countElements().forEach(function (element) {
      updateElement(element, itemCount);
    });
    publish(itemCount, source);
  }

  function cartJsonUrl() {
    var cartUrl = (window.routes && window.routes.cartUrl) || '/cart';
    return cartUrl.replace(/\/$/, '') + '.js';
  }

  function sync(source) {
    if (requestInFlight) return requestInFlight;

    requestInFlight = fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to fetch basket');
        return response.json();
      })
      .then(function (cart) {
        render(parseCount(cart.item_count), source || 'cart.js');
        return cart;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        requestInFlight = null;
      });

    return requestInFlight;
  }

  function scheduleSync(source, delay) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      sync(source);
    }, typeof delay === 'number' ? delay : 120);
  }

  function handleProductAdded(event) {
    var quantity = parseCount(event.detail && event.detail.quantity) || 1;
    var current = lastKnownCount;

    if (current === null) {
      var element = countElements()[0];
      current = element ? parseCount(element.textContent) : 0;
    }

    render(current + quantity, 'product:added');
    scheduleSync('product:added:confirmed', 350);

    if (legacyCartMode) {
      window.setTimeout(function () {
        try {
          if (typeof window.SLIDECART_UPDATE === 'function') {
            window.SLIDECART_UPDATE(function () {
              if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
            });
          } else if (typeof window.SLIDECART_OPEN === 'function') {
            window.SLIDECART_OPEN();
          }
        } catch (error) {}
      }, 80);
    }
  }

  function registerLegacyCartBridge() {
    if (!legacyCartMode) return;

    function chainCallback(name) {
      var previous = window[name];
      window[name] = function (cart) {
        if (typeof previous === 'function') {
          try { previous.apply(window, arguments); } catch (error) {}
        }
        if (cart && typeof cart.item_count !== 'undefined') {
          render(parseCount(cart.item_count), 'legacy-slidecart');
        } else {
          scheduleSync('legacy-slidecart', 100);
        }
      };
    }

    chainCallback('SLIDECART_UPDATED');
    chainCallback('SLIDECART_LOADED');

    document.addEventListener('click', function (event) {
      if (!event.target.closest || !event.target.closest('#slidecarthq')) return;
      window.setTimeout(function () { scheduleSync('legacy-slidecart:click', 0); }, 350);
      window.setTimeout(function () { scheduleSync('legacy-slidecart:click-confirmed', 0); }, 900);
    }, true);
  }

  function init() {
    var elements = countElements();
    if (!elements.length) return;

    render(parseCount(elements[0].textContent), 'init:dom');
    document.addEventListener('product:added', handleProductAdded);
    document.addEventListener('cart:refresh', function () { scheduleSync('cart:refresh'); });
    document.addEventListener('cart:rerendered', function () { scheduleSync('cart:rerendered', 20); }, true);

    window.addEventListener('pageshow', function () { scheduleSync('pageshow', 40); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') scheduleSync('visibilitychange', 40);
    });

    registerLegacyCartBridge();
    scheduleSync('init:cart.js', 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
